import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CanvasPosition } from "@baton/contracts";

export interface PositionChange { nodeId: string; x: number; y: number }

/**
 * Shared canvas layout: the server's positions are the base, locally dragged nodes win
 * until they have been flushed, and flushes are debounced. Nothing is written to browser
 * storage; a reload simply reads the project's positions again.
 */
export function useCanvasPositions(
  projectId: string | null,
  persisted: readonly CanvasPosition[] | undefined,
  save: (projectId: string, positions: PositionChange[]) => Promise<unknown>,
  debounceMs = 600,
) {
  const [local, setLocal] = useState<Map<string, PositionChange>>(new Map());
  const dirty = useRef(new Map<string, PositionChange>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeProject = useRef(projectId);

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const target = activeProject.current;
    if (!target || dirty.current.size === 0) return;
    const batch = [...dirty.current.values()];
    dirty.current.clear();
    try {
      await save(target, batch);
    } catch {
      // Keep the local layout; the next drag retries. Positions are a convenience, not state.
      for (const change of batch) if (!dirty.current.has(change.nodeId)) dirty.current.set(change.nodeId, change);
    }
  }, [save]);

  useEffect(() => {
    if (activeProject.current !== projectId) {
      void flush();
      activeProject.current = projectId;
      setLocal(new Map());
      dirty.current.clear();
    }
  }, [projectId, flush]);

  useEffect(() => {
    const onHide = () => { void flush(); };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      void flush();
    };
  }, [flush]);

  const applyChanges = useCallback((changes: PositionChange[]) => {
    if (changes.length === 0) return;
    setLocal((current) => {
      const next = new Map(current);
      for (const change of changes) {
        const rounded = { nodeId: change.nodeId, x: Math.round(change.x), y: Math.round(change.y) };
        next.set(rounded.nodeId, rounded);
        dirty.current.set(rounded.nodeId, rounded);
      }
      return next;
    });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), debounceMs);
  }, [flush, debounceMs]);

  /** Server positions that are no longer locally dirty replace the local copy. */
  useEffect(() => {
    if (!persisted) return;
    setLocal((current) => {
      let changed = false;
      const next = new Map(current);
      for (const [nodeId] of current) {
        if (!dirty.current.has(nodeId) && persisted.some((position) => position.nodeId === nodeId)) {
          next.delete(nodeId);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [persisted]);

  const positions = useMemo(() => {
    const merged = new Map<string, { x: number; y: number }>();
    for (const position of persisted ?? []) merged.set(position.nodeId, { x: position.x, y: position.y });
    for (const [nodeId, position] of local) merged.set(nodeId, { x: position.x, y: position.y });
    return merged;
  }, [persisted, local]);

  return { positions, applyChanges, flush };
}
